import React from "react";
import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { Icon } from "../../components/ui/icon";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "../../components/ui/input-group";
import {
  Popover,
  PopoverPopup,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { useIsTouchDevice } from "../../hooks/use-is-touch-device";
import { useIsMobile } from "../../hooks/use-media-query";
import { bridgeKeyboardFocus } from "../../lib/keyboard-focus-bridge";
import { usePrefersReducedMotion } from "../../hooks/use-prefers-reduced-motion";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";
import { durationToken } from "../../lib/motion-tokens";

type ExpandableSearchSize = "xs" | "sm" | "default";

/**
 * What a host holding a `ref` can do to the search: open it (the field is
 * collapsed at rest) and put the caret in it — the one gesture a hotkey
 * needs — and find where it sits in the document, so a host arbitrating
 * between several searches can tell which one the viewer is looking at.
 */
export type ExpandableSearchHandle = {
  focus: () => void;
  readonly element: HTMLElement | null;
};

export type ExpandableSearchProps = Omittable<{
  onValueChange: (value: string) => void;
  /**
   * The value the host holds as applied. A field that mounts with one opens
   * in place showing it and its clear button (without taking focus), so a
   * host that remounts its toolbar never shows an empty search over a
   * narrowed view. A host that later moves it to something the field did not
   * itself report (a reset from elsewhere) is adopted the same way; an
   * adopted empty value collapses an unfocused inline field. Typing reports
   * through `onValueChange` after the debounce and never waits on this prop
   * to echo a keystroke.
   */
  value?: string;
  placeholder?: string;
  size?: ExpandableSearchSize;
  /** Hover tooltip on the collapsed trigger (the expanded field labels
   *  itself). A node, so a host can set a keycap beside the name. */
  tooltip?: React.ReactNode;
  ref?: React.Ref<ExpandableSearchHandle>;
  /**
   * How the field appears. `inline` (default) swaps the trigger for the field
   * in place, so the row makes room for it. `overlay` anchors the field in a
   * popover instead: the trigger keeps one footprint in every state, for
   * toolbars whose siblings must never move — and while a value is applied
   * the closed trigger holds the selected-chip tint so the narrowing stays
   * visible. On a phone every search is an overlay, and that overlay is a
   * BAND, not a capsule: the popup spans the screen under the toolbar on
   * the page's 16px gutters, the field runs at the default rung (a thumb
   * target, 16px type so Safari never zooms), and a Done word at its end
   * closes it — the Search key on the phone's keyboard does the same. The
   * list stays in view under the band, narrowing as the viewer types, which
   * is why the phone treatment is not a sheet: a sheet would rise over the
   * very rows the search is narrowing, and the viewer would type blind.
   */
  expand?: "inline" | "overlay";
}>;

// Collapsed trigger footprint per size, and the input size the expanded field
// runs at. Each pair lands on the SAME height (the input + the group's 1px
// borders = the icon button), so expanding never changes the row's height —
// a toolbar must not move when the search opens.
const TRIGGER_SIZE: Record<ExpandableSearchSize, "icon-xs" | "icon-sm" | "icon"> = {
  xs: "icon-xs",
  sm: "icon-sm",
  default: "icon",
};
const INPUT_SIZE: Record<ExpandableSearchSize, "xs" | "sm" | "default"> = {
  xs: "xs",
  sm: "sm",
  default: "default",
};

// The trailing addon takes its gutter from the input's rung; the leading one
// cannot. That gutter rides a `[data-size=<rung>] + &` selector, which only
// ever reaches the addon FOLLOWING the input in DOM order — a leading addon
// precedes it, so it takes the group's base gutter however small the field
// is. Restating the rung here is what puts the lens the same distance from
// its cap as the clear cross sits from the other one.
const LEADING_GUTTER: Record<ExpandableSearchSize, string> = {
  xs: "ps-[calc(--spacing(2)-1px)]",
  sm: "ps-[calc(--spacing(2.5)-1px)]",
  default: "",
};

// The width the inline field grows out of and shrinks back into: the
// collapsed trigger's footprint. A lone glyph makes the icon button exactly
// its `min-w`, and each rung steps down one at `sm`, so these restate the
// TRIGGER_SIZE widths at the same breakpoint. Written out per size because
// Tailwind only emits a class it has seen verbatim.
const COLLAPSED_WIDTH: Record<ExpandableSearchSize, string> = {
  xs: "[--expandable-search-from:--spacing(7)] sm:[--expandable-search-from:--spacing(6)]",
  sm: "[--expandable-search-from:--spacing(8)] sm:[--expandable-search-from:--spacing(7)]",
  default:
    "[--expandable-search-from:--spacing(9)] sm:[--expandable-search-from:--spacing(8)]",
};

// The phone band's inset from the screen's edges: the page's own 16px
// gutter, so the band lines up with the content under it. The popup's
// collision padding takes the same value — the band is anchored to a trigger
// at the toolbar's trailing end, so it is the shift off the screen's edge
// that lands it on the gutter, not its alignment.
const PHONE_BAND_GUTTER_PX = 16;

// The memo takes React's own shallow compare. A custom comparator that skipped
// `onValueChange` also skipped the render that refreshes `onValueChangeRef`, so
// a callback closing over changing state (a filter object, a page cursor) kept
// reporting against the state it was born with — and only unstuck itself if the
// placeholder or size happened to change too.

/** How long typing rests before the value reports out. */
const SEARCH_DEBOUNCE_MS = 250;

export const ExpandableSearch = React.memo(
  function ExpandableSearch({
    onValueChange,
    value: appliedValue,
    placeholder = "Search...",
    size = "sm",
    tooltip,
    expand = "inline",
    ref,
  }: ExpandableSearchProps) {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    // The overlay popover is controlled so the handle can open it: an
    // uncontrolled popover opens from its trigger alone.
    const [overlayOpen, setOverlayOpen] = React.useState(false);
    const hasValueRef = React.useRef(Boolean(appliedValue));
    const justClearedRef = React.useRef(false);
    // The last value handed to the host (or queued for it). The host echoing
    // that value back through `value` is not a change to adopt.
    const reportedRef = React.useRef(appliedValue ?? "");
    // Set by the open gestures (click, handle) so the field takes focus only
    // when a person opened it — a field seeded open by `value` does not.
    const focusOnOpenRef = React.useRef(false);
    // Keeps `handleValueChange` (and everything built on it) referentially
    // stable while still reporting through the newest callback.
    const onValueChangeRef = React.useRef(onValueChange);
    onValueChangeRef.current = onValueChange;
    // The inline expand is animated, so it has three phases, not two:
    // "open" and "closing" both render the field — "closing" keeps it
    // mounted just long enough to play the shrink back into the trigger's
    // footprint before the swap.
    const [phase, setPhase] = React.useState<"collapsed" | "open" | "closing">(
      appliedValue ? "open" : "collapsed",
    );
    const expanded = phase === "open";
    const [value, setValue] = React.useState(appliedValue ?? "");
    const isMobile = useIsMobile();
    const touch = useIsTouchDevice();
    const overlay = expand === "overlay" || isMobile;
    // The phone band's field is out of the toolbar row, so the row-height
    // pairing that ties the field to the trigger's rung does not bind it:
    // it takes the default rung whatever size the trigger runs at.
    const fieldSize: ExpandableSearchSize = isMobile ? "default" : size;
    const reducedMotion = usePrefersReducedMotion();

    const debounceRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

    const handleValueChange = React.useCallback((v: string) => {
      const hadValue = hasValueRef.current;
      hasValueRef.current = v.length > 0;
      setValue(v);

      // Value was cleared via the clear button — stay expanded and re-focus
      if (hadValue && v.length === 0) {
        justClearedRef.current = true;
        requestAnimationFrame(() => {
          inputRef.current?.focus();
          justClearedRef.current = false;
        });
      }

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        reportedRef.current = v;
        React.startTransition(() => onValueChangeRef.current(v));
      }, SEARCH_DEBOUNCE_MS);
    }, []);

    React.useEffect(() => () => clearTimeout(debounceRef.current), []);

    // Every collapse path funnels through here. With motion suppressed there
    // is no shrink to wait for, so the field goes straight out rather than
    // sitting mounted for the span an animation would have taken.
    // Neither updater needs a phase guard: both are only reachable from the
    // mounted field (a blur, Escape, or an outside click while the listener
    // is attached), and a redundant call sets the phase it already holds,
    // which React bails out of.
    const beginCollapse = React.useCallback(() => {
      setPhase(reducedMotion ? "collapsed" : "closing");
    }, [reducedMotion]);

    const finishCollapse = React.useCallback(() => {
      setPhase("collapsed");
    }, []);

    // The unmount is keyed to the shrink animation's end; this lands the
    // collapse anyway if that event never arrives. The cleanup means a phase
    // change cancels it, so it can never fire against a reopened field.
    React.useEffect(() => {
      if (phase !== "closing") return;
      const timer = setTimeout(finishCollapse, durationToken("slow"));
      return () => clearTimeout(timer);
    }, [phase, finishCollapse]);

    const collapse = React.useCallback(() => {
      if (!hasValueRef.current && !justClearedRef.current) beginCollapse();
    }, [beginCollapse]);

    const handleClick = React.useCallback(() => {
      if (!expanded) {
        focusOnOpenRef.current = true;
        setPhase("open");
      }

      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }, [expanded]);

    // The handle's `focus` is the trigger's click: inline opens the field in
    // place, overlay opens the popover (whose `initialFocus` lands the
    // caret). An already-open field just takes focus back.
    React.useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          if (overlay) {
            setOverlayOpen(true);
            requestAnimationFrame(() => {
              inputRef.current?.focus();
            });
            return;
          }
          handleClick();
        },
        get element() {
          return containerRef.current;
        },
      }),
      [overlay, handleClick],
    );

    // Keyboard navigation: Tab away collapses the search
    const handleBlur = React.useCallback(() => {
      collapse();
    }, [collapse]);

    // Escape clears a populated field, then collapses an empty one. Clearing
    // CONSUMES the key: the field may sit inside a dismissable layer (its own
    // overlay popover, a dialog), and a clear that also propagated would close
    // that layer on the same press — throwing away the "second Escape closes"
    // rhythm. An empty field lets the key through: inline collapses itself,
    // overlay leaves the close to the popover's own dismiss.
    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Enter commits an overlay: the value is applied as it is typed, so
        // the key has nothing left to do but close the popup — on a phone it
        // is the keyboard's Search key, the way out of the band besides
        // Done. The pending report is not dropped by the close; it lands.
        if (e.key === "Enter" && overlay) {
          e.preventDefault();
          setOverlayOpen(false);
          return;
        }
        if (e.key !== "Escape") return;
        if (hasValueRef.current) {
          e.preventDefault();
          e.stopPropagation();
          handleValueChange("");
          return;
        }
        if (!overlay) beginCollapse();
      },
      [handleValueChange, overlay, beginCollapse],
    );

    React.useLayoutEffect(() => {
      if (!expanded || !focusOnOpenRef.current) return;
      focusOnOpenRef.current = false;
      inputRef.current?.focus();
    }, [expanded]);

    // Adopt a host-side move of the applied value. A pending report is
    // dropped with it: the host has spoken since the keystroke it answers.
    React.useEffect(() => {
      if (appliedValue === undefined || appliedValue === reportedRef.current) {
        return;
      }
      clearTimeout(debounceRef.current);
      reportedRef.current = appliedValue;
      hasValueRef.current = appliedValue.length > 0;
      setValue(appliedValue);
      if (appliedValue) {
        if (!overlay) setPhase("open");
      } else if (!overlay && document.activeElement !== inputRef.current) {
        beginCollapse();
      }
    }, [appliedValue, overlay, beginCollapse]);

    // Collapse when clicking outside the container
    React.useEffect(() => {
      if (!expanded) return;

      const handleMouseUp = (e: MouseEvent) => {
        const node = containerRef.current;
        if (node && !node.contains(e.target as Node)) {
          requestAnimationFrame(() => collapse());
        }
      };

      document.addEventListener("mouseup", handleMouseUp);
      return () => document.removeEventListener("mouseup", handleMouseUp);
    }, [expanded, collapse]);

    // Toolbar search is always a pill: circular collapsed trigger,
    // rounded-full field — matching the pill toolbar chips. Block-level
    // `flex` (not the group's inline-flex): in the overlay, the popover
    // viewport is a block container, and an inline-level field rides its
    // text line — the line's strut pads the capsule with descender space on
    // one vertical edge, so the gutter never measures equal no matter what
    // padding the popup takes. A block-level field makes the capsule exactly
    // gutter + pill + gutter.
    // Inline only: the capsule grows out of the collapsed trigger's footprint
    // on open and shrinks back into it on close (the overlay popover animates
    // itself). The input's min-w-0 lets it shrink to nothing, so mid-animation
    // the group reads as the icon circle stretching into the field.
    const searchField = (
      <InputGroup
        className={cn(
          // The phone band's field fills the band and keeps the input's own
          // corners: it is a framed search band, the grammar every phone
          // sheet's search wears, not a toolbar pill.
          isMobile ? "flex min-w-0 flex-1" : "flex w-48 rounded-full",
          !overlay && [
            COLLAPSED_WIDTH[size],
            "motion-reduce:animate-none",
            phase === "closing"
              ? "animate-expandable-search-out"
              : "animate-expandable-search-in",
          ],
        )}
        onAnimationEnd={
          overlay
            ? undefined
            : (e: React.AnimationEvent<HTMLDivElement>) => {
                if (e.animationName === "expandable-search-out")
                  finishCollapse();
              }
        }
      >
        <InputGroupAddon className={LEADING_GUTTER[fieldSize]}>
          <Icon name="search" />
        </InputGroupAddon>
        <InputGroupInput
          ref={inputRef}
          type="search"
          size={INPUT_SIZE[fieldSize]}
          placeholder={placeholder}
          value={value}
          onValueChange={handleValueChange}
          // Inline collapses when focus leaves an empty field; overlay leaves
          // dismissal to the popover, and closing never discards the value.
          onBlur={overlay ? undefined : handleBlur}
          onKeyDown={handleKeyDown}
        />
        {value ? (
          <InputGroupAddon align="inline-end">
            <Button
              variant="ghost"
              size="icon-xs"
              // icon-xs (24px desktop) is taller than the xs field's 22px
              // inner row and would stretch the group — the very jump the
              // size pairing exists to prevent — so the clear chip caps at
              // 20px there. Every rung of the icon size has to be answered:
              // it writes a height AND a min-width at both breakpoints, and
              // `size-*` out-merges only the height half.
              //
              // The addon's trailing gutter is cut for a 24px chip, so the
              // 20px one gives half the difference back as margin: that
              // lands the chip concentric with the pill's trailing cap. The
              // small optical shift centers the cross's drawn ink against
              // the longer search mark at the other end.
              className={
                fieldSize === "xs"
                  ? "size-5 min-w-5 sm:size-5 sm:min-w-5 me-0.5 rounded-full [&_svg]:-translate-x-[0.333px]"
                  : "rounded-full [&_svg]:-translate-x-[0.333px]"
              }
              aria-label="Clear search"
              onClick={() => handleValueChange("")}
            >
              <Icon name="x" />
            </Button>
          </InputGroupAddon>
        ) : null}
      </InputGroup>
    );

    if (overlay) {
      const overlayTrigger = (
        <Button
          // The applied value tints the closed trigger as a selected chip —
          // the popover hides the field, so the trigger is the only place the
          // active narrowing can show.
          color={value ? "primary" : "neutral"}
          variant={value ? "outline" : "ghost"}
          size={TRIGGER_SIZE[size]}
          className="rounded-full"
          aria-label={value ? `Search, filtering by "${value}"` : "Search"}
          // The tap on the lens promised typing, but the field it opens
          // mounts a frame later, and iOS raises the keyboard only for a
          // focus made inside the tap itself: the bridge takes focus now, and
          // the popup's `initialFocus` hands it to the field with the
          // keyboard already up. A tap that CLOSES the popup brings none.
          onClick={() => {
            if (touch && !overlayOpen) bridgeKeyboardFocus();
          }}
        >
          <Icon name="search" />
        </Button>
      );
      return (
        // The same row wrapper as inline, so the handle has one element to
        // report whichever way the field expands.
        <Box ref={containerRef} direction="row" align="center">
          <Popover open={overlayOpen} onOpenChange={setOverlayOpen}>
            {tooltip ? (
              <Tooltip>
                <TooltipTrigger
                  render={<PopoverTrigger render={overlayTrigger} />}
                />
                <TooltipPopup side="top">{tooltip}</TooltipPopup>
              </Tooltip>
            ) : (
              <PopoverTrigger render={overlayTrigger} />
            )}
          {/* Focus lands in the field on every open — the popup's default
            * only moves focus for keyboard opens, and a search popover that
            * opens without a caret is a dead click. The popup is a capsule:
            * it wraps the pill field on a uniform gutter, so its corners run
            * concentric with the field's rounded-full instead of the stock
            * popover radius cutting across them. The gutter must out-size the
            * field's 3px focus ring — at 4px the ring kisses the popup edge
            * and the pill reads clipped. */}
            <PopoverPopup
              align="end"
              className={
                isMobile ? undefined : "rounded-full before:rounded-full"
              }
              collisionPadding={isMobile ? PHONE_BAND_GUTTER_PX : undefined}
              initialFocus={inputRef}
              padding={isMobile ? "sm" : "xs"}
              style={
                isMobile
                  ? { width: `calc(100vw - ${2 * PHONE_BAND_GUTTER_PX}px)` }
                  : undefined
              }
            >
              {isMobile ? (
                // The band: the field, then Done. Done closes the band and
                // keeps the value (the trigger tints to show it); the clear
                // cross inside the field is what empties it.
                <Box direction="row" align="center" gap="sm">
                  {searchField}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setOverlayOpen(false)}
                  >
                    Done
                  </Button>
                </Box>
              ) : (
                searchField
              )}
            </PopoverPopup>
          </Popover>
        </Box>
      );
    }

    const collapsedTrigger = (
      <Button
        variant="ghost"
        size={TRIGGER_SIZE[size]}
        className="rounded-full"
        aria-label="Search"
        onClick={handleClick}
      >
        <Icon name="search" />
      </Button>
    );

    return (
      <Box ref={containerRef} direction="row" align="center">
        {phase !== "collapsed" ? (
          searchField
        ) : tooltip ? (
          <Tooltip>
            <TooltipTrigger render={collapsedTrigger} />
            <TooltipPopup side="top">{tooltip}</TooltipPopup>
          </Tooltip>
        ) : (
          collapsedTrigger
        )}
      </Box>
    );
  },
);

ExpandableSearch.displayName = "ExpandableSearch";
